import i18n from '../../i18n/i18n';
import { tr } from '../../i18n/i18n';
/**
 * SessionRequiredPage Component
 *
 * Purpose: Inline component displayed by SessionGuard when no active payment session exists.
 * Rendered within MainLayout's content area (navigation and header remain visible).
 *
 * Design: 403-inspired centered card with clean, focused layout for professional UX.
 * Uses Classveew purple theme colors for brand consistency.
 *
 * Features:
 * - Contained card design (white box with shadow) for visual prominence
 * - Users can navigate to other sections via sidebar while card is displayed
 * - Clear status indicator and concise messaging
 * - Progressive access - users decide when to start session
 * - SessionGuard handles session checks and re-rendering
 */

import React, { useRef } from 'react';
import { AppButton } from '../../components/atoms';
import { ModalHandle } from '../../components/Modals';
import { useAppDispatch } from '../../redux/stateHooks';
import { startSession } from '../../redux/action/payment.action';
import { showNotification } from '../../redux/slices/notification.slice';
import StartSessionModal from '../../components/payment-session/StartSessionModal';
import { useTranslation } from 'react-i18next';

const SessionRequiredPage: React.FC = () => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const startSessionModalRef = useRef<ModalHandle>(null);

  const handleStartSession = () => {
    startSessionModalRef.current?.openModal();
  };

  const handleStartSessionSubmit = async (data: { openingBalance: number; notes: string }) => {
    try {
      // Dispatch startSession Redux thunk (automatically updates activeSession in Redux)
      await dispatch(
        startSession({
          opening_balance: data.openingBalance,
          session_notes: data.notes,
        }),
      ).unwrap();

      // Show success notification
      dispatch(
        showNotification({
          message: i18n.t(
            'msg.payment_session_started_successfully',
            'Payment session started successfully',
          ),
          severity: 'success',
        }),
      );

      // Modal will auto-close via ref, SessionGuard will automatically re-render when Redux state updates
      // NO NEED for getActiveSession() - startSession already updated Redux state!
    } catch (error: any) {
      // Show error notification
      dispatch(
        showNotification({
          message:
            error.message ||
            tr('g.failed_to_start_payment_session', 'Failed to start payment session'),
          severity: 'error',
        }),
      );
    }
  };

  return (
    <>
      {/* Centered Container */}
      <div className="flex items-center justify-center min-h-[calc(100vh-200px)] px-4 py-8">
        {/* 403-Inspired Card/Box Container */}
        <div className="bg-white rounded-xl shadow-lg p-10 max-w-md w-full text-center">
          {/* Lock Icon with Purple Gradient Background */}
          <div className="flex items-center justify-center w-24 h-24 mx-auto mb-5 rounded-full bg-gradient-to-br from-purple-100 to-purple-200">
            <i className="pi pi-lock text-4xl text-purple-600" />
          </div>

          {/* Status Indicator (like "403" number in reference design) */}
          <div className="text-3xl font-bold text-purple-600 mb-3">
            {t('billing.session_required', 'SESSION REQUIRED')}
          </div>

          {/* Title */}
          <h1 className="text-xl font-semibold mb-3 text-gray-800">
            {t('payment_session.start_your_session', 'Start Your Cashier Session')}
          </h1>

          {/* Description (concise, single line) */}
          <p className="text-gray-500 mb-8">
            {t(
              'g.you_need_an_active_cashier_session_to_access_invoice_ma',
              'You need an active cashier session to access invoice management',
            )}
          </p>

          {/* Start Session Button */}
          <AppButton
            label={t('common.button_start_session', 'Start Session')}
            icon="pi pi-arrow-right"
            iconPos="right"
            onClick={handleStartSession}
            className="w-full px-8 py-3"
            severity="help"
          />
        </div>
      </div>

      {/* Start Session Modal (reusable component) */}
      <StartSessionModal ref={startSessionModalRef} onSubmit={handleStartSessionSubmit} />
    </>
  );
};

export default SessionRequiredPage;
